import { useState, useCallback, useEffect, useMemo } from 'react'
import { AuthContext } from './auth.context'
import { getAuthCookies, clearAuthCookies } from '../../services/auth-cookies/auth-cookies.service'
import { isTokenValid, getUserFromToken, isTemporaryUserEmail } from '../../services/jwt/jwt.service'
import { getAuthConfig } from '../../services/auth-config/auth-config.service'
import { refreshAuthTokens } from '../../services/token-refresh/token-refresh.service'
import type { TAuthState, TAuthProviderConfig } from './auth.types'

const authenticatedState = (idToken: string): TAuthState => ({
  isAuthenticated: true,
  isLoading: false,
  user: getUserFromToken(idToken),
  token: idToken,
})

const unauthenticatedState: TAuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  token: null,
}

const pendingRefreshState: TAuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  token: null,
}

const resolveInitialState = (): TAuthState => {
  const { idToken, refreshToken } = getAuthCookies()

  if (idToken && isTokenValid(idToken)) {
    return authenticatedState(idToken)
  }

  if (refreshToken) {
    return pendingRefreshState
  }

  return unauthenticatedState
}

export const AuthProvider = ({ children }: TAuthProviderConfig) => {
  const [authState, setAuthState] = useState<TAuthState>(resolveInitialState)

  const refreshAuth = useCallback(async () => {
    const { idToken, refreshToken } = getAuthCookies()

    if (idToken && isTokenValid(idToken)) {
      setAuthState(authenticatedState(idToken))
      return
    }

    if (refreshToken) {
      try {
        const newIdToken = await refreshAuthTokens()
        setAuthState(authenticatedState(newIdToken))
        return
      } catch {
        clearAuthCookies()
      }
    }

    setAuthState(unauthenticatedState)
  }, [])

  const logout = useCallback(async () => {
    const cookies = getAuthCookies()
    const config = getAuthConfig()

    const username = cookies.idToken ? getUserFromToken(cookies.idToken)?.email : undefined

    if (username) {
      try {
        const { authHttpClient } = await import('../../services/auth-http-client/auth-http-client.service')
        const baseUrl = config.apiUrl.endsWith('/') ? config.apiUrl : `${config.apiUrl}/`
        await authHttpClient.post(`${baseUrl}v2/login/signout`, {
          username,
        })
      } catch (error) {
        console.warn('Signout API failed, proceeding with local logout', error)
      }
    }

    clearAuthCookies()

    setAuthState(unauthenticatedState)

    if (globalThis.location !== undefined) {
      const returnUrl = encodeURIComponent(globalThis.location.href)
      globalThis.location.href = `${config.loginUrl}?r=${returnUrl}`
    }
  }, [])

  useEffect(() => {
    if (!authState.isLoading) return

    let cancelled = false

    refreshAuthTokens()
      .then((newIdToken) => {
        if (!cancelled) setAuthState(authenticatedState(newIdToken))
      })
      .catch(() => {
        if (!cancelled) {
          clearAuthCookies()
          setAuthState(unauthenticatedState)
        }
      })

    return () => {
      cancelled = true
    }
  }, [authState.isLoading])

  const userEmail = authState.user?.email
  const isTemporaryUser = userEmail ? isTemporaryUserEmail(userEmail) : false

  const contextValue = useMemo(
    () => ({
      ...authState,
      logout,
      refreshAuth,
      isTemporaryUser,
    }),
    [authState, logout, refreshAuth, isTemporaryUser],
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

AuthProvider.displayName = 'AuthProvider'
